<template>
  <div :class="{'calendar': true, 'day-selectable': daySelectable && selectedDay}" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd">
    <div class="year-month">
      <span @click="addMonth(-1, $event)" class="calendar-arrow">
        &lt;
      </span>
      <span v-if="showYear" @click="toggleEditYearMonth($event)">
        {{ $t('calendar.format.year', {year: year}) }}
      </span>
      &nbsp;
      <span @click="toggleEditYearMonth($event)">
        {{ $t('calendar.format.month', {month: month + 1}) }}
      </span>
      <span @click="addMonth(1, $event)" class="calendar-arrow">
        &gt;
      </span>
      <div class="caret-down" />
    </div>
    <div class="clear" />
    <div v-if="yearMonthShown" class="year-month-edit-box">
      <input v-model="yearInput" type="number" min="2021" max="3021" class="form-control">
      <b-dropdown class="form-control">
        <template #button-content>
          {{ monthInput + 1 }}
        </template>
        <b-dropdown-item v-for="(mon, monIndex) in months" :key="'month-' + monIndex" @click="monthInput = monIndex;">
          {{ monIndex + 1 }}
        </b-dropdown-item>
      </b-dropdown>
      <div class="clear" />
      <button class="btn btn-primary form-control" @click="applyYearMonth($event)">
        {{ $t('calendar.button.apply') }}
      </button>
      <button class="btn btn-default form-control" @click="toggleEditYearMonth($event)">
        {{ $t('calendar.button.close') }}
      </button>
    </div>
    <div class="clear" />
    <div v-if="showWeekDays" class="week-days">
      <div v-if="!mondayFirst" class="week-day">
        {{ $t('calendar.weekday.sunday') }}
      </div>
      <div class="week-day">
        {{ $t('calendar.weekday.monday') }}
      </div>
      <div class="week-day">
        {{ $t('calendar.weekday.tuesday') }}
      </div>
      <div class="week-day">
        {{ $t('calendar.weekday.wednesday') }}
      </div>
      <div class="week-day">
        {{ $t('calendar.weekday.thursday') }}
      </div>
      <div class="week-day">
        {{ $t('calendar.weekday.friday') }}
      </div>
      <div class="week-day">
        {{ $t('calendar.weekday.saturday') }}
      </div>
      <div v-if="mondayFirst" class="week-day">
        {{ $t('calendar.weekday.sunday') }}
      </div>
      <div class="clear" />
    </div>
    <div v-for="(week, weekIndex) in weeks" :key="'week-' + weekIndex" :class="{'month-days': true, 'hidden': showOnlySelectedPeriod && !isInPeriodWeek(week)}">
      <div v-for="(day, dayIndex) in week" :key="'day-' + dayIndex" :class="'month-day ' + getPeriodsCss(value, day) + (isSelectedDay(day) ? ' clicked' : '') + (daySelectable && day === selectedDay ? ' day-selected' : '') + (isDisabledDay(day) ? ' disabled' : '')" @click="onClickDay(day, $event)">
        <div class="period-background-line first-half" />
        <div class="period-background-line second-half" />
        <div v-if="day > 0" class="date">
          {{ day }}
        </div>
      </div>
      <div class="clear" />
    </div>
    <div v-if="showPeriodText && selectedPeriod" class="period-text">
      <div class="start-date-text">
        <div class="period-text-title">
          <div class="dot" />
          {{ $t('calendar.text.startdate') }}
        </div>
        <div class="period-text-contents">
          {{ selectedPeriod.bgnde.replaceAll('-', '.').replaceAll('/', '.') }}
        </div>
      </div>
      <div class="end-date-text">
        <div class="period-text-title">
          <div class="dot" />
          {{ $t('calendar.text.enddate') }}
        </div>
        <div class="period-text-contents">
          {{ selectedPeriod.endde.replaceAll('-', '.').replaceAll('/', '.') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    showYear: {
      type: Boolean,
      default: true
    },
    showPeriodText: {
      type: Boolean,
      default: false
    },
    mondayFirst: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: undefined
    },
    disabledPeriods: {
      type: Array,
      default: undefined
    },
    mode: {
      type: String,
      default: 'viewer'
    },
    daySelectable: {
      type: Boolean,
      default: false
    },
    showOnlySelectedPeriod: {
      type: Boolean,
      default: false
    },
    showWeekDays: {
      type: Boolean,
      default: true
    },
    initYearMonthByPeriod: {
      type: Boolean,
      default: false
    },
    emitDayEventOnClick: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      year: 1980,
      month: 0,
      day1: undefined,
      day2: undefined,
      selectedDay: undefined,
      months: [
        this.$t('calendar.month.1'),
        this.$t('calendar.month.2'),
        this.$t('calendar.month.3'),
        this.$t('calendar.month.4'),
        this.$t('calendar.month.5'),
        this.$t('calendar.month.6'),
        this.$t('calendar.month.7'),
        this.$t('calendar.month.8'),
        this.$t('calendar.month.9'),
        this.$t('calendar.month.10'),
        this.$t('calendar.month.11'),
        this.$t('calendar.month.12')
      ],
      weeks: [],
      yearMonthShown: false,
      yearInput: 1980, // 입력용
      monthInput: 0, // 입력용
      selectedPeriod: undefined,
      dateformat: this.$t('common.format.date')
    }
  },
  watch: {
    value: {
      handler(newVal, oldVal) {
        this.$forceUpdate()
      },
      immediate: true,
      deep: true
    },
    selectedPeriod: {
      handler(newVal, oldVal) {
        this.$emit('period-selected', newVal)
      },
      immediate: true,
      deep: true
    },
    selectedDay: {
      handler(newVal, oldVal) {
        if (!this.emitDayEventOnClick) {
          this.$emit('day-selected', {
            period: this.selectedPeriod,
            year: this.year,
            month: this.month,
            day: newVal
          })
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.today = new Date()
    const today = this.today
    let endde
    let bgnde
    if (this.initYearMonthByPeriod && this.value && this.value.length > 0) {
      for (let i = 0; i < this.value.length; i++) {
        const period = this.value[i]
        if (!bgnde || bgnde < period.bgnde) {
          endde = period.endde
          bgnde = period.bgnde
        }
      }
      if (bgnde) {
        const bgndeDt = this.$moment(bgnde, this.dateformat).toDate()
        if (this.daySelectable) {
          this.selectedDay = bgndeDt.getDate()
        }
        this.year = bgndeDt.getFullYear()
        this.month = bgndeDt.getMonth()
      }
    }
    if (!endde) {
      this.year = today.getFullYear()
      this.month = today.getMonth()
    }
    this.yearInput = this.year
    this.monthInput = this.month
    this.onMonthChanged()
    this.displayMonth()
    this.initSelected()
  },
  methods: {
    isDisabledDay(day) {
      if (!this.disabledPeriods || this.disabledPeriods.length === 0) {
        return false
      }
      let date = this.year + '/'
      if (this.month + 1 < 10) {
        date += '0'
      }
      date += this.month + 1 + '/'
      if (day < 10) {
        date += '0'
      }
      date += day
      for (let i = 0; i < this.disabledPeriods.length; i++) {
        const period = this.disabledPeriods[i]
        if (period.bgnde <= date && period.endde >= date) {
          return true
        }
      }
      if (this.day1) {
        let date1 = this.day1.year + '/'
        if (this.day1.month + 1 < 10) {
          date1 += '0'
        }
        date1 += this.day1.month + 1 + '/'
        if (this.day1.day < 10) {
          date1 += '0'
        }
        date1 += this.day1.day
        let minBgnde
        let maxEndde
        for (let i = 0; i < this.disabledPeriods.length; i++) {
          const period = this.disabledPeriods[i]
          if (period.bgnde >= date1 && (!minBgnde || minBgnde > period.bgnde)) {
            minBgnde = period.bgnde
          }
          if (period.endde <= date1 && (!maxEndde || maxEndde < period.endde)) {
            maxEndde = period.endde
          }
        }
        if (date > minBgnde) {
          return true
        }
        if (date < maxEndde) {
          return true
        }
      }
      return false
    },
    isSelectedDay(day) {
      if (
        this.day1 &&
        this.year === this.day1.year &&
        this.month === this.day1.month &&
        day === this.day1.day
      ) {
        return true
      }
      if (
        this.day2 &&
        this.year === this.day2.year &&
        this.month === this.day2.month &&
        day === this.day2.day
      ) {
        return true
      }
      return false
    },
    toggleEditYearMonth(e) {
      if (e) {
        if (e.preventDefault) {
          e.preventDefault()
        }
        if (e.stopPropagation) {
          e.stopPropagation()
        }
      }
      this.vibrate(e)
      if (!this.yearMonthShown) {
        this.yearInput = this.year
        this.monthInput = this.month
      }
      this.yearMonthShown = !this.yearMonthShown
      this.$forceUpdate()
    },
    addMonth(diff, e) {
      if (e) {
        if (e.preventDefault) {
          e.preventDefault()
        }
        if (e.stopPropagation) {
          e.stopPropagation()
        }
      }
      this.vibrate(e)
      if (this.monthInput + diff > 11) {
        this.monthInput = this.monthInput + diff - 12
        this.yearInput++
      } else if (this.monthInput + diff < 0) {
        this.monthInput = this.monthInput + diff + 12
        this.yearInput--
      } else {
        this.monthInput = this.monthInput + diff
      }
      console.log('year : ' + this.yearInput)
      console.log('month : ' + this.monthInput)
      this.applyYearMonth()
    },
    applyYearMonth(e) {
      if (e) {
        if (e.preventDefault) {
          e.preventDefault()
        }
        if (e.stopPropagation) {
          e.stopPropagation()
        }
      }
      this.vibrate(e)
      this.year = this.yearInput
      this.month = this.monthInput
      this.yearMonthShown = false
      this.selectedDay = undefined

      this.onMonthChanged()
      this.$forceUpdate()
      this.displayMonth()
      this.initSelected()
    },
    onMonthChanged() {
      this.$emit('month-changed', {
        year: this.year,
        month: this.month
      })
    },
    onClickDay(day, e) {
      if (e) {
        if (e.preventDefault) {
          e.preventDefault()
        }
        if (e.stopPropagation) {
          e.stopPropagation()
        }
      }
      if (this.isDisabledDay(day)) {
        return
      }
      if (this.mode === 'viewer') {
        const period = this.getCurrentPeriod(day)
        if (!period) {
          return
        }
        this.vibrate(e)
        if (this.selectedPeriod !== period) {
          this.selectedPeriod = period
          this.$forceUpdate()
        } else if (this.daySelectable) {
          this.selectedDay = day
          if (this.emitDayEventOnClick) {
            this.$emit('day-selected', {
              period: this.selectedPeriod,
              year: this.year,
              month: this.month,
              day: day
            })
          }
        }
      } else if (this.mode === 'period-selector') {
        let updated = false
        if (
          this.day1 &&
          this.year === this.day1.year &&
          this.month === this.day1.month &&
          day === this.day1.day
        ) {
          this.day1 = undefined
          updated = true
        } else if (
          this.day2 &&
          this.year === this.day2.year &&
          this.month === this.day2.month &&
          day === this.day2.day
        ) {
          this.day2 = undefined
          updated = true
        }
        if (this.day1 && this.day2) {
          this.day1 = undefined
          this.day2 = undefined
        }
        if (!updated) {
          if (!this.day1) {
            this.$emit('input', [])
            this.$emit('change', [])
            this.day1 = {
              year: this.year,
              month: this.month,
              day: day
            }
          } else if (!this.day2) {
            this.day2 = {
              year: this.year,
              month: this.month,
              day: day
            }
          }
        }
        if (this.day1 && this.day2) {
          const dt1 = new Date(this.day1.year, this.day1.month, this.day1.day)
          const dt2 = new Date(this.day2.year, this.day2.month, this.day2.day)
          const bgnde = this.$moment(
            dt1.getTime() < dt2.getTime() ? dt1 : dt2
          ).format(this.$t('common.format.date'))
          const endde = this.$moment(
            dt1.getTime() > dt2.getTime() ? dt1 : dt2
          ).format(this.$t('common.format.date'))
          const param = [
            {
              bgnde: bgnde,
              endde: endde
            }
          ]
          this.day1 = undefined
          this.day2 = undefined
          this.$emit('input', param)
          this.$emit('change', param)
          this.$nextTick(() => {
            this.initSelected()
            this.$forceUpdate()
          })
        }
      }
    },
    getCurrentPeriod(day) {
      const periods = this.value
      if (!periods) {
        return
      }
      for (let i = 0; i < periods.length; i++) {
        const period = periods[i]
        const compde = new Date(this.year, this.month, day)
        if (this.isInPeriod(period, compde)) {
          return period
        }
      }
    },
    getPeriodsCss(periods, day) {
      if (!periods || !day) {
        return ''
      }
      let clRet = ''
      for (let i = 0; i < periods.length; i++) {
        const period = periods[i]
        const compde = new Date(this.year, this.month, day)
        let cl = this.isInPeriod(period, compde)
        if (cl) {
          if (period === this.selectedPeriod) {
            cl = cl + ' selected ' + cl + '-selected'
          }
          clRet += ' ' + cl
        }
      }
      return clRet
    },
    isInPeriodWeek(week) {
      for (let i = 0; i < week.length; i++) {
        if (this.getCurrentPeriod(week[i])) {
          return true
        }
      }
      return false
    },
    isInPeriod(period, compde) {
      const dateformat = this.dateformat
      const bgnde = this.$moment(period.bgnde, dateformat).toDate()
      const endde = this.$moment(period.endde, dateformat).toDate()
      if (
        bgnde.getTime() === compde.getTime() &&
        endde.getTime() === compde.getTime()
      ) {
        return 'start-end-date'
      }
      if (bgnde.getTime() === compde.getTime()) {
        return 'start-date'
      }
      if (endde.getTime() === compde.getTime()) {
        return 'end-date'
      }
      if (
        bgnde.getTime() < compde.getTime() &&
        endde.getTime() > compde.getTime()
      ) {
        return 'in-period'
      }
    },
    onTouchStart(e) {
      this.touching = true
      this.touchStartX = e.touches[0].clientX
      this.touchStartY = e.touches[0].clientY
      this.touchX = e.touches[0].clientX
      this.touchY = e.touches[0].clientY
    },
    onTouchMove(e) {
      if (this.touching) {
        this.touchX = e.touches[0].clientX
        this.touchY = e.touches[0].clientY
      }
    },
    onTouchEnd(e) {
      if (this.touching) {
        this.touching = false
        let diffX = this.touchX - this.touchStartX
        let diffY = this.touchY - this.touchStartY
        let monthDiff = -1
        if (diffX < 0) {
          diffX = -diffX
          monthDiff = 1
        }
        if (diffY < 0) {
          diffY = -diffY
        }
        if (diffX > diffY && diffX > 30) {
          if (!this.yearMonthShown) {
            this.yearInput = this.year
            this.monthInput = this.month
          }
          this.addMonth(monthDiff, e)
        }
      }
    },
    displayMonth() {
      this.weeks = []
      this.thisMonthDays = new Date(this.year, this.month + 1, 0).getDate()
      this.thisMonthFirstDay = new Date(this.year, this.month, 1).getDay()
      if (this.mondayFirst) {
        this.thisMonthFirstDay--
        if (this.thisMonthFirstDay < 0) {
          this.thisMonthFirstDay = 6
        }
      }
      let week = []
      let weekday = this.thisMonthFirstDay
      for (let i = 0; i < this.thisMonthDays; i++) {
        if (i === 0) {
          for (let j = 0; j < this.thisMonthFirstDay; j++) {
            week.push(0)
          }
        }
        week.push(i + 1)
        if (weekday === 6) {
          weekday = 0
          this.weeks.push(week)
          week = []
        } else {
          weekday++
        }
      }
      if (week.length > 0) {
        this.weeks.push(week)
      }
    },
    initSelected() {
      this.selectedPeriod = undefined
      // 오늘이 포함된 기간이 있으면 먼저 선택
      this.selectedPeriod = this.getCurrentPeriod(this.today.getDate())

      if (!this.selectedPeriod) {
        // 기간 선택(첫번째로 일치하는 기간)
        for (let i = 0; i < this.thisMonthDays; i++) {
          this.selectedPeriod = this.getCurrentPeriod(i + 1)
          if (this.selectedPeriod) {
            break
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.year-month {
  display: flex;
  justify-content: center;
  font-size: 1.25rem;
}
.caret-down {
  float: right;
  margin-top: 1rem;
  margin-left: 0.8rem;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0.791666666666667rem 0.458333333333333rem 0 0.458333333333333rem;
  border-color: #ffffff transparent transparent transparent;
}
.week-days {
  margin: 1rem 0.5rem;
  display: flex;
}
.week-day {
  width: calc(100% / 7);
  text-align: center;
  font-size: 1rem;
  color: #528ce2;
}
.month-days {
  margin: 0.5rem;
  display: flex;
}
.hidden {
  display: none !important;
  margin: 0 !important;
}
.month-day {
  width: calc(100% / 7);
  text-align: center;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.month-day.disabled {
  color: #aaaaaa;
}
.date {
  width: 2.94rem;
  height: 2.94rem;
  border-radius: 1.5rem;
  overflow: hidden;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: #d8d8d8;
}
.period-background-line {
  position: absolute;
  width: 50%;
  height: 2.65rem;
}
.period-background-line.first-half {
  left: 0;
}
.period-background-line.second-half {
  right: 0;
}
.period-background-line {
  display: none;
}

.start-end-date .date,
.clicked .date,
.start-date .date {
  background: #4d87e1;
  color: white;
}
.start-end-date-selected .date,
.start-date.start-date-selected .date {
  background: #360ea6;
  color: white;
}
.end-date .date {
  background: #ababab;
  color: white;
}
.end-date.end-date-selected .date {
  background: #6b6b6b;
}

.day-selectable .clicked .date,
.day-selectable .start-end-date .date,
.day-selectable .start-date .date,
.day-selectable .end-date .date,
.day-selectable .start-date-selected .date,
.day-selectable .end-date-selected .date {
  background: gray;
}
.day-selectable .day-selected .date {
  background: #03ccc8;
}
.start-date .period-background-line.second-half,
.end-date .period-background-line.first-half,
.in-period .period-background-line,
.in-period.start-date .period-background-line.first-half,
.in-period.end-date .period-background-line.second-half {
  display: block;
  background: rgba(0, 0, 0, 0.05);
  color: black;
}
.selected.start-date .period-background-line.second-half,
.selected.end-date .period-background-line.first-half,
.selected.in-period .period-background-line,
.selected.in-period.start-date .period-background-line.first-half,
.selected.in-period.end-date .period-background-line.second-half {
  display: block;
  background: rgba(0, 0, 0, 0.2);
  color: black;
}
.in-period .date {
  color: black;
}
.start-end-date .period-background-line.second-half,
.start-end-date .period-background-line.first-half {
  display: none;
}

.calendar-arrow {
  font-size: 1.8rem;
  padding: 0 1.5rem;
  margin-top: -0.3rem;
}

/* 달력 */
.year-month-edit-box {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 0.5rem;
  z-index: 10;
}
.year-month-edit-box .form-control {
  margin: 0.5rem;
  width: calc(100% - 1rem);
}
.year-month-edit-box input.form-control,
.year-month-edit-box .b-dropdown.form-control {
  float: left;
  width: calc(50% - 1rem);
  background: transparent;
  color: white;
}
.year-month-edit-box .b-dropdown.form-control {
  padding-left: 0;
  padding-right: 0;
}
.year-month-edit-box .b-dropdown >>> .btn.dropdown-toggle {
  background-color: transparent !important;
  padding: 0;
  height: unset;
  width: 100%;
  border: none;
  text-align: left;
  padding-left: 1rem;
}
.year-month-edit-box .b-dropdown >>> .btn.dropdown-toggle::after {
  float: right;
  margin-top: 0.6rem;
  margin-right: 1rem;
}
.year-month-edit-box .b-dropdown >>> .dropdown-menu {
  min-width: unset;
  right: 0;
  border-radius: 1rem;
  background-color: rgba(248, 249, 250, 0.9);
}
.year-month-edit-box .b-dropdown >>> .dropdown-item {
  font-size: 1.666667rem;
  color: #808080;
  text-align: center;
  padding: 0.2rem 0;
  border-bottom: 1px solid #dddddd;
}
.year-month-edit-box .b-dropdown >>> .btn.dropdown-toggle:focus,
.year-month-edit-box .b-dropdown >>> .btn.dropdown-toggle:hover,
.year-month-edit-box .b-dropdown >>> .btn.dropdown-toggle:active {
  color: white;
  border: none;
  outline: none !important;
  box-shadow: none !important;
}
.period-text {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
.period-text .start-date-text,
.period-text .end-date-text {
  margin-top: 1.191666666666667rem;
  margin-bottom: 1.191666666666667rem;
  background: white;
  color: black;
  height: 2.233333333333333rem;
  display: flex;
  width: 100%;
  justify-content: space-between;
}
.period-text .start-date-text {
  margin-bottom: 0;
}
.period-text-title {
  width: 6.15rem;
  height: 2.233333333333333rem;
  font-size: 1rem;
  line-height: 1.4rem;
  position: relative;
  border-radius: 0.7rem;
  padding-left: 1.15rem;
  margin-left: 2rem;
}
.period-text-contents {
  font-size: 1rem;
  height: 2.233333333333333rem;
  width: calc(100% - 12.375rem);
  border: 1px solid #c5c5c5;
  border-radius: 1.108333333333333rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 2rem;
}
.period-text-title .dot {
  background: #360ea6;
  width: 0.608333333333333rem;
  height: 0.608333333333333rem;
  position: absolute;
  border-radius: 0.2rem;
  top: 0.4rem;
}
.period-text-title .dot:first-child {
  left: 0.2rem;
}
.period-text-title .dot:last-child {
  right: 0.2rem;
}
.btn-previous-month img,
.btn-next-month img {
  width: 1.3rem;
}
.month-buttons > button {
  width: calc(50% - 0.5rem);
  height: 2.833333333333333rem;
}
.month-buttons {
  display: flex;
  justify-content: space-between;
  padding: 0 0.5rem 0.5rem;
}
</style>
